import { PublicationContext, Subscription } from 'centrifuge'
import { isEqual } from 'lodash'
import { Schema } from 'yup'
import { ZodType as ZodSchema } from 'zod'

import { InitializeSubscriptionParams } from './types'

const lastMessages: Record<string, unknown[] | unknown> = {}

const MULTI_MESSAGES_LIMIT = 20

export function getParsedData<TResult>(value: unknown, schema: Schema<TResult> | ZodSchema<TResult>) {
  if (schema instanceof ZodSchema) {
    return schema.parse(value)
  }

  return schema.validateSync(value)
}

export function initializeSubscription({
  webSocketInstance,
  channel,
  onPublication,
  multi,
}: InitializeSubscriptionParams) {
  const subscriptionOnPublicationFn = (context: PublicationContext) => {
    const lastMessage = lastMessages[channel]

    if (isEqual(lastMessages[channel], context.data)) {
      return
    }

    if (multi) {
      const currentLastMessages = lastMessage ? (lastMessage as unknown[]) : []

      setLastMessage(channel, [context.data, ...currentLastMessages].slice(0, MULTI_MESSAGES_LIMIT))
    } else {
      setLastMessage(channel, context.data)
    }

    onPublication(lastMessages[channel])
  }

  let currentSubscription = webSocketInstance.getSubscription(channel)

  if (currentSubscription) {
    currentSubscription.on('publication', subscriptionOnPublicationFn)

    publishLastValue(currentSubscription, onPublication, multi)
  } else {
    const subscription = webSocketInstance.newSubscription(channel)
    currentSubscription = subscription

    subscription.on('publication', subscriptionOnPublicationFn)

    subscription.on('subscribing', (context) => {
      console.debug(`Subscribing: ${context.code}, ${context.reason}`)
    })

    subscription.on('subscribed', (context) => {
      console.debug('Subscribed to: ', context)

      publishLastValue(currentSubscription, onPublication, multi)
    })

    subscription.on('unsubscribed', (context) => {
      console.debug(`Unsubscribed: ${context.code}, ${context.reason}`)
    })

    subscription.subscribe()
  }

  return currentSubscription
}

async function publishLastValue(
  currentSubscription: Subscription | null,
  onPublication: (message: unknown) => void,
  multi = false,
) {
  const value = await getValue(currentSubscription, multi)

  if (value) {
    if (currentSubscription) {
      setLastMessage(currentSubscription.channel, value)
    }

    onPublication(value)
  }
}

async function getValue(currentSubscription: Subscription | null, multi = false) {
  const data = await currentSubscription?.history({ limit: MULTI_MESSAGES_LIMIT, reverse: true })
  const values = (data?.publications ?? []).map((publication) => publication.data)

  if (multi) {
    return values
  }

  return values[0]
}

function setLastMessage(channel: string, values: unknown) {
  lastMessages[channel] = values
}
